import {
	ChangeDetectorRef,
	Component,
	inject,
	Input,
	OnInit,
	ViewChild,
	AfterViewInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
	PdfModalComponent,
	PdfModalData,
} from '@components/pdf-modal/pdf-modal.component';
import { ICON } from '@core/util/icon';
import { getRequisitionFieldsFromParcelInstrument } from '@core/util/requisitions';
import { SortDirection } from '@models/common';
import { Encumbrance } from '@models/encumbrance';
import { MatterFileType } from '@models/matter';
import {
	MatterRequisitionPrecedent,
	RequisitionPrecedentFieldNames,
} from '@models/requisitions';
import {
	InstrumentAttachment,
	InstrumentSorting,
	ParcelInstrument,
	SavedInstrument,
	SavedParcel,
	SavedPin,
} from '@models/teranet';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select, Store } from '@ngxs/store';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterRequisitionsState } from '@state/matter/requisitions/matter-requisitions.state';
import { SaleInstrumentsState } from '@state/sale_instruments';
import { InstrumentsDispatch } from '@state/sale_instruments/instruments.dispatch';
import { SelectedAttachmentDocument } from '@state/sale_instruments/models';
import { SalePinState } from '@state/sale_pins';
import { UserProfileState } from '@state/user-profile';
import { SetTeranetSettingsSorting } from '@state/user-profile/actions';
import {
	PurchaseState,
	PurchaseStateType,
	PurchaseStates,
} from '@state/utils/models';
import { cloneDeep, groupBy, orderBy, uniq } from 'lodash';
import { Observable, combineLatest } from 'rxjs';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { NewRequisitionModalComponent } from '../../documents/requisitions/modals/matter-requisition/new-requisition-modal/new-requisition-modal.component';
import {
	PurchaseInstrumentComponent,
	PurchaseInstrumentModalData,
} from './purchase-instrument/purchase-instrument.component';
import {
	TeranetCreateEncumbranceComponent,
	TeranetCreateEncumbranceData,
} from './teranet-create-encumbrance/teranet-create-encumbrance.component';
import { DEFAULT_REQUISITION_DIMENSIONS } from '../../documents/requisitions/modals';
import { UpdateRequisitionModalComponent } from '../../documents/requisitions/modals/matter-requisition/update-requisition-modal/update-requisition-modal.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { TestHooks } from './instruments.hooks';

interface InstrumentModel extends ParcelInstrument {
	saved: SavedInstrument;
	purchaseState: PurchaseState;
	selected: boolean;
	encumbranceUrl?: string;
	showEncumbrance: boolean;
}

const ShowEncumbranceList = [
	'CHARGE',
	'NOTICE',
	'LIEN',
	'TRANSFER OF CHARGE',
	'POSTPONEMENT',
	'NO SEC INTEREST',
];

enum State {
	ADDED = 'added',
	DELETED = 'deleted',
	SAME = 'same',
}

export enum TypeFilterValue {
	NONE = 'NONE',
	TRANSFER = 'TRANSFER',
	CHARGE = 'CHARGE',
}

const FilterItems: RadioGroupItem<TypeFilterValue>[] = [
	{ label: 'All', value: TypeFilterValue.NONE },
	{ label: 'Charge', value: TypeFilterValue.CHARGE },
	{ label: 'Transfer', value: TypeFilterValue.TRANSFER },
];

interface Filter {
	showDeleted: boolean;
	type: TypeFilterValue;
}

@UntilDestroy()
@Component({
	selector: 'app-teranet-instruments',
	templateUrl: './instruments.component.html',
	styleUrls: ['./instruments.component.scss'],
	standalone: false,
})
export class TeranetInstrumentsComponent implements OnInit, AfterViewInit {
	testHooks = TestHooks;
	private hasInitialized = false;
	drawerService = inject(DrawerService);

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@SelectSnapshot(SelectedMatter.matterNumber) matterNumber: string;
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: string;
	@ViewSelectSnapshot(SalePinState.getPrimary) primary: SavedPin;
	@Select(SalePinState.getSelected) selectedPin$: Observable<SavedPin>;
	@Select(SaleInstrumentsState.getItems) selectedInstruments$: Observable<
		SavedInstrument[]
	>;
	@ViewSelectSnapshot(SaleInstrumentsState.getInstrumentPurchaseState)
	instrumentPurchaseState: PurchaseStates;
	@ViewSelectSnapshot(SaleInstrumentsState.getAttachmentPurchaseState)
	attachmentsPurchaseState: PurchaseStates;
	@Select(MatterEncumbrancesState.getAll) encumbrances$: Observable<
		Encumbrance[]
	>;
	@ViewSelectSnapshot(SaleInstrumentsState.getSearching) searching: boolean;
	@Select(MatterRequisitionsState.getAll) requisitions$: Observable<
		MatterRequisitionPrecedent[]
	>;
	@ViewChild(MatSort) sort: MatSort;

	@SelectSnapshot(UserProfileState.teranetInstrumentSortField)
	sortField?: string;
	@SelectSnapshot(UserProfileState.teranetInstrumentSortDirection)
	sortDirection?: SortDirection;

	ICON = ICON;
	public matterFileTypes = MatterFileType;
	public _filteredInstruments: ParcelInstrument[];
	public showDeleted = false;
	public _typeFilter = TypeFilterValue.NONE;
	public typeFilters = TypeFilterValue;
	public filterItems = FilterItems;
	public purchaseState = PurchaseStateType;
	public selectedPin: SavedPin;

	public state = State;
	public dataSource = new MatTableDataSource();
	public displayColumns = [
		'exclamation',
		'fileIcon',
		'state',
		'reg_number',
		'reg_date',
		'type',
		'from',
		'to',
		'status',
		'amount',
		'showEncumbrance',
	];

	public items: InstrumentModel[] = [];
	public filteredItems: InstrumentModel[] = [];
	public showFetchDocuments = false;

	public selectedInstruments: { [id: string]: string };
	public selectedAttributeItems: { [id: string]: SelectedAttachmentDocument } =
		{};

	public displayedColumns: string[] = [
		'error',
		'state',
		'select',
		'id',
		'date',
		'type',
		'partiesFrom',
		'partiesTo',
		'status',
		'amount',
		'requisition',
	];

	private globalFilter: Filter = {
		showDeleted: this.showDeleted,
		type: this._typeFilter,
	};

	public requisitionsInstrumentNumbers = new Map<string | number, string>();

	@Input() showRequisition = false;

	@Input()
	set filterType(type: TypeFilterValue) {
		this._typeFilter = type;
		this.globalFilter.type = type;
	}

	constructor(
		private dialog: MatDialog,
		private instrumentsDispatch: InstrumentsDispatch,
		private cd: ChangeDetectorRef,
		private store: Store,
	) {}

	ngOnInit(): void {
		combineLatest([
			this.selectedPin$,
			this.selectedInstruments$,
			this.encumbrances$,
			this.requisitions$,
		])
			.pipe(untilDestroyed(this))
			.subscribe(
				([
					_selectedPin,
					_selectedInstruments,
					_encumbrances,
					_requisitions,
				]) => {
					this.setInstruments(
						_selectedPin,
						_selectedInstruments,
						_encumbrances,
					);
					this.selectedPin = _selectedPin;

					this.showFetchDocuments =
						!_selectedInstruments?.length ||
						!_selectedInstruments?.every((x) => x.attachments);

					for (const req of _requisitions) {
						const instrumentNumber =
							req?.fields?.[RequisitionPrecedentFieldNames.INSTRUMENT_NUMBER]
								?.value;
						if (instrumentNumber) {
							this.requisitionsInstrumentNumbers.set(instrumentNumber, req.id);
						}
					}
				},
			);
	}

	ngAfterViewInit() {
		this.dataSource.sort = this.sort;

		this.dataSource.sortingDataAccessor = (item: InstrumentModel, headerId) => {
			switch (headerId) {
				case 'from':
					return item.parties_from?.join(' ');
				case 'to':
					return item.parties_to?.join(' ');
				default:
					return item[headerId];
			}
		};

		this.sort.active = this.sortField;
		this.sort.direction = this.sortDirection;
		this.sort.sortChange.emit();

		this.hasInitialized = true;
		this.cd.detectChanges();
	}

	onSortChange(sort: MatSort) {
		if (sort && this.hasInitialized) {
			const sorting: InstrumentSorting = {
				direction: sort.direction,
				field: sort.active,
			};
			this.store.dispatch(new SetTeranetSettingsSorting(sorting));
		}
	}

	addInstrument() {
		const data: PurchaseInstrumentModalData = {
			pin: this.selectedPin,
		};

		this.dialog.open(PurchaseInstrumentComponent, { data });
	}

	trackByInstrId(index: number, item: InstrumentModel) {
		return item.reg_number;
	}

	verifyInstrumentState(instrument: InstrumentModel): boolean {
		return (
			!instrument?.saved?.uploaded_document_path &&
			this.instrumentPurchaseState[instrument.reg_number]?.state !==
				PurchaseStateType.PURCHASING
		);
	}

	toggleSelectedInstrument(select: boolean, instrument: InstrumentModel) {
		if (select && this.verifyInstrumentState(instrument)) {
			this.selectedInstruments[instrument.reg_number] = instrument.reg_number;
		} else {
			delete this.selectedInstruments[instrument.reg_number];
		}
	}

	get selectedCount() {
		const instrumentsCount = Object.keys(this.selectedInstruments ?? {}).length;
		const attributeItemsCount = Object.keys(
			this.selectedAttributeItems ?? {},
		).length;

		return instrumentsCount + attributeItemsCount;
	}

	searchDocuments() {
		this.instrumentsDispatch.searchInstruments(
			this.selectedPin.pin,
			this.items.map((x) => x.reg_number),
		);
	}

	toggleSelectedAttribute(
		select: boolean,
		instrument: InstrumentModel,
		attachment: InstrumentAttachment,
	) {
		if (select) {
			this.addSelectedAttributeItem(
				attachment,
				instrument?.saved?.registration_number,
			);
		} else {
			this.removeSelectedAttributeItem(attachment.document_number);
		}
	}

	addSelectedAttributeItem(
		attachment: InstrumentAttachment,
		instrumentNumber: string,
	) {
		this.selectedAttributeItems[attachment.document_number] = {
			instrumentNumber,
			attachment,
			selectedPin: this.selectedPin.pin,
		};
	}

	removeSelectedAttributeItem(documentId: string) {
		this.selectedAttributeItems[documentId] = undefined;
	}

	setInstruments(
		pin: SavedPin,
		instruments: SavedInstrument[],
		encumbrances: Encumbrance[],
	): InstrumentModel[] {
		if (pin) {
			if (!pin?.parcels) {
				this.items = [];
				this.filterItems = [];
				this.setFilteredItems();

				return;
			}

			let _instr = cloneDeep(pin.parcels[0].instruments) as InstrumentModel[];

			const extra = instruments
				.filter(
					(x) =>
						x.pin === pin.pin &&
						!_instr.some((y) => y.reg_number === x.instrument_number),
				)
				?.map((x) => ({
					reg_number: x.instrument_number,
					dateSearched: new Date(),
				}));

			_instr.push(...(extra as unknown as InstrumentModel[]));

			const lastInstrumentNumbers = uniq(
				pin.parcels.map((x) => x.latestInstrumentNumber),
			);

			if (lastInstrumentNumbers?.length > 1) {
				const allInstruments = [];
				const _mergedInstruments = [];

				pin.parcels.forEach((parcel: SavedParcel) => {
					parcel.instruments.forEach((instrument) =>
						allInstruments.push({ ...instrument, date: parcel.dateSearched }),
					);
				});

				const groupedInstruments = groupBy(allInstruments, 'reg_number');
				/* eslint-disable @typescript-eslint/no-unused-vars */
				for (const [key, value] of Object.entries(groupedInstruments)) {
					const ordered: ParcelInstrument[] = orderBy(value, [
						'dateSearched',
						'desc',
					]);
					let state: State = State.SAME;

					if ((value as ParcelInstrument[]).length === pin.parcels.length - 1) {
						state = State.ADDED;
					} else if (ordered[0].status !== ordered[ordered.length - 1].status) {
						state = State.DELETED;
					}

					_mergedInstruments.push({ ...ordered[0], state });
				}

				_instr = _mergedInstruments;
			}

			_instr.forEach((x) => {
				const saved = instruments.find(
					(y) => y.registration_number === x.reg_number,
				);
				if (saved) {
					x.saved = saved;
				}

				const encumbrance = encumbrances.find(
					(_enc) =>
						x.reg_number?.length > 0 &&
						_enc.registrationNumber?.toLowerCase() ===
							x.reg_number?.toLowerCase(),
				);
				if (encumbrance?.id) {
					x.encumbranceUrl = UrlService.Matter_Encumbrances_Encumbrance(
						this.matterId,
						encumbrance.id,
					);
				}

				x.showEncumbrance = ShowEncumbranceList.includes(x.type);
			});

			this.items = orderBy(
				_instr,
				(val) => (val?.reg_date ? new Date(val?.reg_date) : new Date()),
				'desc',
			);

			this.filterItems = FilterItems;
			this.setFilteredItems();
		}
	}

	toggleShowDeleted(show: boolean) {
		this.showDeleted = show;
		this.setFilteredItems();
	}

	setType(value: TypeFilterValue) {
		this._typeFilter = value;
		this.setFilteredItems();
	}

	get selectAll() {
		const instCount =
			this.filteredItems?.filter((x) => this.verifyInstrumentState(x)).length ??
			0;

		if (instCount === 0) return false;

		return Object.keys(this.selectedInstruments ?? {}).length === instCount;
	}

	toggleAllInstruments = (selected: boolean) => {
		if (selected) {
			this.filteredItems?.forEach((x) =>
				this.toggleSelectedInstrument(true, x),
			);
		} else {
			this.selectedInstruments = {};
			this.selectedAttributeItems = {};
		}
	};

	setFilteredItems = () => {
		this.selectedInstruments = {};
		this.selectedAttributeItems = {};
		this.filteredItems = this.items
			?.filter((x) => this.showDeleted || x.status !== 'D')
			.filter(
				(x) =>
					this._typeFilter === TypeFilterValue.NONE ||
					x.type === this._typeFilter,
			);
		this.dataSource.data = this.filteredItems?.map((x) => ({
			...x,
		}));
	};

	isAnAttachment = (index, item) =>
		!!Object.values(item.saved?.attachments || {})?.length;

	purchaseSelected = () => {
		this.instrumentsDispatch.purchaseInstrumentDocuments(
			this.selectedPin.pin,
			Object.keys(this.selectedInstruments),
		);
		this.instrumentsDispatch.purchaseAttachmentDocuments(
			Object.values(this.selectedAttributeItems),
		);

		this.clearSelected();
	};

	showInstrumentPDF(url: string, reg_number: string, type: string) {
		const fileName = `${type}_${reg_number}`.replace(/\./g, '');
		const data: PdfModalData = {
			googleSrcUrl: url,
			fileName,
		};

		this.drawerService.openDrawer(PdfModalComponent, {
			data,
			title: fileName,
			noStyle: true,
		});
	}

	addEncumbrance(instrument: ParcelInstrument) {
		const data: TeranetCreateEncumbranceData = {
			instrumentType: instrument.type,
			registrationNumber: instrument.reg_number,
			registrationDate: instrument.reg_date,
		};

		this.dialog.open(TeranetCreateEncumbranceComponent, { data });
	}

	addRequisition(instrument: ParcelInstrument) {
		const fields = getRequisitionFieldsFromParcelInstrument(instrument);

		this.dialog.open(NewRequisitionModalComponent, {
			...DEFAULT_REQUISITION_DIMENSIONS,
			data: { fields },
		});
	}

	editRequisition(instrument: ParcelInstrument) {
		this.dialog.open(UpdateRequisitionModalComponent, {
			...DEFAULT_REQUISITION_DIMENSIONS,
			data: {
				selectedRequisitionId: this.requisitionsInstrumentNumbers.get(
					instrument.reg_number,
				),
			},
		});
	}

	clearSelected() {
		this.selectedInstruments = {};
		this.selectedAttributeItems = {};
	}

	trackByIndex(index) {
		return index;
	}
}
